import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { AlertCircle } from 'lucide-react';

const Input = forwardRef(({
  label,
  error,
  hint,
  required = false,
  icon: Icon,
  iconPosition = 'left',
  className = '',
  containerClassName = '',
  type = 'text',
  ...props
}, ref) => {
  const inputClasses = [
    'w-full px-4 py-2.5 rounded-lg border transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-0',
    'disabled:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-60',
    error
      ? 'border-danger-300 text-danger-900 focus:border-danger-500 focus:ring-danger-500'
      : 'border-neutral-300 text-neutral-900 focus:border-primary-500 focus:ring-primary-500 hover:border-neutral-400',
    Icon && iconPosition === 'left' ? 'pl-10' : '',
    Icon && iconPosition === 'right' ? 'pr-10' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={`form-group ${containerClassName}`}>
      {label && (
        <label className="form-label flex items-center gap-1">
          {label}
          {required && <span className="text-danger-500">*</span>}
        </label>
      )}

      <div className="relative">
        {Icon && iconPosition === 'left' && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
            <Icon className="w-5 h-5" />
          </div>
        )}

        <input
          ref={ref}
          type={type}
          className={inputClasses}
          {...props}
        />

        {Icon && iconPosition === 'right' && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>

      {error && (
        <div className="form-error flex items-center gap-1 mt-1">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {hint && !error && (
        <div className="form-hint mt-1">
          {hint}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  label: PropTypes.string,
  error: PropTypes.string,
  hint: PropTypes.string,
  required: PropTypes.bool,
  icon: PropTypes.elementType,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  type: PropTypes.string,
};

export default Input;
